import {Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {DatePipe} from '@angular/common';
import {AdxBookingToTramsInvoiceLink} from '../../models/adx-booking-to-trams-invoice-link';
import {SearchPaginatedAdxBookingToTramsInvoiceLinkResponse} from '../../models/search-paginated-adx-booking-to-trams-invoice-link-response';
import {MatDialog} from '@angular/material/dialog';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';
import {AdxBookingToTramsInvoiceLinkGridService} from '../../service/adx-booking-to-trams-invoice-link-grid.service';
import {AdxBookingDetailsModalComponent} from 'src/app/adx-booking/components/adx-booking-details-modal/adx-booking-details-modal.component';
import {TramsInvoiceModalComponent} from 'src/app/invoices/component/trams-invoice-modal/trams-invoice-modal.component';

@Component({
    selector: 'adx-booking-to-trams-invoice-link-list',
    templateUrl: './adx-booking-to-trams-invoice-link-list.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class AdxBookingToTramsInvoiceLinkListComponent implements OnInit {
  @Input() public data: any;
  @ViewChild(PageGridComponent) pageGrid?: PageGridComponent;

  isTogglePending = false;

  private adxBookingToTramsInvoiceLinkGridService = inject(AdxBookingToTramsInvoiceLinkGridService)
  private datePipe = inject(DatePipe);
  private dialog = inject(MatDialog);

  protected readonly adxBookingToTramsInvoiceLinksGrid: GridModel<SearchPaginatedAdxBookingToTramsInvoiceLinkResponse> = {
    gridDataTestId: 'adx-booking-to-trams-invoice-link-list',
    gridTitle: 'Trams Invoice Links',
    replayButton: true,
    isTotalsRowHidden: true,
    defaultSort: { active: 'id', direction: 'asc' },
    columns: [
      {
        columnProperty: 'adxBooking.travelType.travelTypeName',
        columnHeader: 'Travel Type',
        sort: true,
        columnPropertyFunction: (item: AdxBookingToTramsInvoiceLink) => item.adxBooking?.travelType?.travelTypeName || '',
      },
      {
        columnProperty: 'adxBooking.externalTravelServiceType.name',
        columnHeader: 'External Travel Type',
        sort: true,
        columnPropertyFunction: (item: AdxBookingToTramsInvoiceLink) => item.adxBooking?.externalTravelServiceType?.name || '-',
      },
      {
        columnProperty: 'adxBookingId',
        columnHeader: 'Adx Service Number',
        sort: true,
        clickable: true,
        style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
        linkEventFunction: (item: AdxBookingToTramsInvoiceLink) => `AdxBookings/${item.adxBookingId}`,
      },
      {
        columnProperty: 'isFinal',
        columnHeader: 'Final Payment',
        sort: true,
        columnIconDisplayFunction: (item: AdxBookingToTramsInvoiceLink) => item.isFinal ? "checkmark" : "clear"
      },
      {
        columnProperty: 'invoiceType.invoiceTypeName',
        columnHeader: 'Invoice Type',
        sort: true,
        columnPropertyFunction: (item: AdxBookingToTramsInvoiceLink) => item.invoiceType?.invoiceTypeName || '',
      },
      {
        columnProperty: 'tramsInvoiceNumber',
        columnHeader: 'Trams Invoice Number',
        sort: true,
        clickableWithItem: (item: AdxBookingToTramsInvoiceLink) => !!item.tramsInvoiceId,
        style: (item: AdxBookingToTramsInvoiceLink) => item.tramsInvoiceId ? ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }) : ({'pointer-events': 'none', 'color': '#000000', 'text-decoration': 'none', 'cursor': 'default'}) as Record<string, string>,
        linkEventFunction: (item: AdxBookingToTramsInvoiceLink) => item.tramsInvoiceId == undefined ? '' : `Invoices/TramsInvoices/${item.tramsInvoiceId}`,
        shouldHideCellContent: (item: AdxBookingToTramsInvoiceLink) => item.tramsInvoiceNumber != 0,
      },
      {
        columnProperty: 'isVoucherApplied',
        columnHeader: 'Voucher Applied',
        sort: true,
        columnIconDisplayFunction: (item: AdxBookingToTramsInvoiceLink) => item.invoiceTypeId == 4 ? (item.isVoucherApplied ? "checkmark" : "clear") : "remove",
      },
      {
        columnProperty: 'isProcessed',
        columnHeader: 'Processed',
        sort: true,
        columnIconDisplayFunction: (item: AdxBookingToTramsInvoiceLink) => item.isProcessed ? "checkmark" : "clear"
      },
      {
        columnProperty: 'dateUpdated',
        columnHeader: 'Last Updated',
        sort: true,
        columnPropertyFunction: (item: AdxBookingToTramsInvoiceLink) => this.datePipe.transform(item.dateUpdated, 'M/d/y') || '',
      },
        {
          columnProperty: 'isEligible',
          columnHeader: 'Is Eligible',
          sort: true,
          shouldHideColumn: true,
          columnPropertyFunction: (item: AdxBookingToTramsInvoiceLink) => item.isEligible ? 'TRUE' : 'FALSE',
        },
    ],
    filters: [],
    showActionColumn: true,
    actionColumnHeader: 'Is Eligible',
    gridGetService: this.adxBookingToTramsInvoiceLinkGridService
  }

  public ngOnInit(): void {
    if (this.data == undefined) {
      this.data = {
        tripId: null
      }
    }

    this.adxBookingToTramsInvoiceLinksGrid.filtersInitial = {
      tripId: this.data.tripId || null,
    }
  }


  handleIdClick(event: GridEventOutput<AdxBookingToTramsInvoiceLink>): void {
    if(event.column === 'adxBookingId' && event.item.adxBookingId !== undefined){
      this.openAdxBookingDialog(event.item.adxBookingId, `Service information`);
    }
    else if(event.column === 'tramsInvoiceNumber' && event.item.tramsInvoiceId !== undefined){
      this.openTramsInvoiceDialog(event.item.tramsInvoiceId);
    }
  }

  public openAdxBookingDialog(adxBookingId: number, title: string): void {
    this.dialog.open(AdxBookingDetailsModalComponent, {
      width: '55%',
      data: { adxBookingId: adxBookingId, title: title }
    });
  }

  public openTramsInvoiceDialog(tramsInvoiceId: number): void {
    this.dialog.open(TramsInvoiceModalComponent, {
      width: '55%',
      data: { invoiceId: tramsInvoiceId }
    });
  }

  public onToggleIsEligible(item: AdxBookingToTramsInvoiceLink): void {
    this.isTogglePending = true;
    this.adxBookingToTramsInvoiceLinkGridService
      .updateIsEligible({ id: item.id, isEligible: item.isEligible ?? false })
      .subscribe({
        next: () => {
          this.isTogglePending = false;
        },
        error: () => {
          this.pageGrid?.getItems();
          this.isTogglePending = false;
        }
      });
  }
}
